import React from 'react';
import { motion } from 'framer-motion';
import { QrCode } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Escolha um modelo que já funciona.',
    text: 'Comece de uma estrutura salva em vez de abrir um arquivo antigo e reconstruir o documento a cada emissão.',
    mockup: (
      <div className="space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">Modelos</p>
        {['Laudo Piometra', 'Laudo Macho', 'Laudo Fêmea'].map((name, index) => (
          <div key={name} className={`border rounded-card px-4 py-3 flex items-center justify-between ${index === 0 ? 'border-selo bg-selo-tint' : 'border-line bg-paper'}`}>
            <span className="text-sm font-semibold text-ink">{name}</span>
            {index === 0 && <span className="font-mono text-[10px] text-selo">SELECIONADO</span>}
          </div>
        ))}
      </div>
    ),
  },
  {
    number: '02',
    title: 'Preencha o que muda. O resto permanece organizado.',
    text: 'Paciente, local, assinatura e conteúdo ficam no mesmo fluxo. A estrutura reutilizável reduz o trabalho repetido sem tocar no conteúdo clínico.',
    mockup: (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-line rounded-card p-3"><span className="font-mono text-[9px] text-muted">PACIENTE</span><p className="text-sm text-ink mt-1">Nome do paciente</p></div>
          <div className="border border-line rounded-card p-3"><span className="font-mono text-[9px] text-muted">DATA</span><p className="text-sm text-ink mt-1">17/08/2026</p></div>
        </div>
        <div className="border border-selo/30 bg-selo-tint rounded-card p-4">
          <span className="font-mono text-[9px] text-selo">CONTEÚDO DO LAUDO</span>
          <div className="mt-3 space-y-2"><div className="h-2 bg-navy/12 rounded"/><div className="h-2 bg-navy/12 rounded w-11/12"/><div className="h-2 bg-navy/12 rounded w-8/12"/></div>
        </div>
      </div>
    ),
  },
  {
    number: '03',
    title: 'Emita o PDF com sua identidade.',
    text: 'O documento reúne os dados do profissional, local e assinatura visual configurados no Laudando, mantendo apresentação consistente.',
    mockup: (
      <div className="bg-paper border border-line rounded-card p-5 shadow-card">
        <div className="flex justify-between items-start border-b border-line pb-4 mb-4"><div><p className="font-display font-bold text-navy">LAUDO</p><p className="font-mono text-[9px] text-muted mt-1">DOCUMENTO PROFISSIONAL</p></div><div className="w-10 h-10 border border-line rounded-card" /></div>
        <div className="space-y-2"><div className="h-2 bg-paper-alt rounded"/><div className="h-2 bg-paper-alt rounded"/><div className="h-2 bg-paper-alt rounded w-9/12"/></div>
        <div className="mt-6 pt-4 border-t border-line flex justify-between"><span className="font-mono text-[9px] text-muted">PROFISSIONAL</span><span className="font-mono text-[9px] text-muted">REGISTRO</span></div>
      </div>
    ),
  },
  {
    number: '04',
    title: 'Registre integridade e mantenha histórico.',
    text: 'Ao finalizar, o Laudando associa hash SHA-256, código de acesso e dados de emissão ao registro do laudo. Versões e revogação ficam vinculadas ao histórico.',
    mockup: (
      <div className="font-mono text-xs space-y-3">
        <div className="flex justify-between border-b border-line pb-3"><span className="text-muted">SHA-256</span><span className="text-selo">8f3a…c92d</span></div>
        <div className="flex justify-between border-b border-line pb-3"><span className="text-muted">CÓDIGO</span><span className="text-ink">F9D2K4</span></div>
        <div className="flex justify-between"><span className="text-muted">STATUS</span><span className="text-selo">REGISTRADO</span></div>
      </div>
    ),
  },
  {
    number: '05',
    title: 'Entregue por link, QR ou PDF.',
    text: 'O paciente não precisa criar conta para verificar o registro. O documento pode ser compartilhado e continua disponível no histórico do profissional.',
    mockup: (
      <div className="grid grid-cols-[1fr_auto] gap-5 items-center">
        <div><p className="font-mono text-[10px] text-muted">VALIDAÇÃO ONLINE</p><p className="font-display text-lg font-bold text-navy mt-2">Documento registrado</p><p className="text-xs text-muted mt-2">Código F9D2K4</p></div>
        <div className="w-24 h-24 border-2 border-navy rounded-card flex items-center justify-center"><QrCode className="w-16 h-16 text-navy" aria-hidden="true" /></div>
      </div>
    ),
  },
];

const Features = () => (
  <section id="recursos" className="bg-paper py-24 md:py-32 px-4">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl mb-16">
        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-[-0.025em] text-navy">Cada etapa deixa a próxima <em className="font-semibold text-selo">menos repetitiva.</em></h2>
      </div>

      <div className="space-y-20 md:space-y-28">
        {steps.map((step, index) => (
          <motion.article key={step.number} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${index % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
            <div>
              <p className="font-mono text-xs text-selo mb-4">{step.number}</p>
              <h3 className="font-display text-2xl md:text-4xl font-bold text-navy tracking-[-0.02em] mb-5">{step.title}</h3>
              <p className="text-base md:text-lg text-muted leading-relaxed max-w-xl">{step.text}</p>
            </div>
            <div className="bg-paper-alt border border-line rounded-card p-5 md:p-8">{step.mockup}</div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

export default Features;
